import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
     CDropdown,
     CDropdownItem,
     CDropdownMenu,
     CDropdownHeader,
     CDropdownToggle,
     CBadge,
     CHeaderNav,
     CButton,
     CSmartTable,
     CDropdownDivider,
} from '@coreui/react-pro';

import { signOut } from '../../helpers/auth';
import { switchOrg } from '../../helpers/subscriber';
import { addTimezoneLocal, getURLParams, tryParseJSON } from '../../utils';
import { LOCAL_CART, LOCAL_ORG_USER, LOCAL_COUPON, ORG_USER_ROLES, MY_LEARNING_PATH, SUBSCRIBER_PATH, LOCAL_COUPON_VOUCHERS } from '../../constants';

import CreateOrganization from '../../components/CMS/Subscriber/CreateOrganization';
import LoginAs from '../../components/Auth/LoginAs';

import { setOverlay } from '../../actions/general';
import { fetchManagedOrganizationsRequest } from '../../components/CMS/Organizations/ManagedOrganizations/actions';
import { setListOrganizationSeats, setOrganizationSeatsFilter } from '../../components/CMS/Organizations/ManageSeats/OrganizationSeats/actions';
import { fetchOrganizationCoursesRequest } from '../../components/CMS/Organizations/OrganizationCourses/actions';

import { setCurrentCountry } from '../../components/CMS/Subscriber/GlobalStates/CurrentCountry/actions';
import { setOrganizationOrders } from '../../components/CMS/Organizations/Purchases/Orders/action';
import { setOrganization } from '../../components/CMS/Organizations/Purchases/PaymentMethod/action';
import { actionClearCart } from '../../components/CMS/Guest/Cart/actions';

const SubscriberAccountDropdown = () => {
     const dispatch = useDispatch();
     const navigate = useNavigate();
     const location = useLocation();
     const params = getURLParams();

     const user = useSelector((state) => state.subscriber.user);
     const managedOrganizations = useSelector((state) => state.subscriber.managedOrganizations);
     const organizationCourses = useSelector((state) => state.subscriber.organizationCourses);

     const [showCreateOrg, setShowCreateOrg] = useState(false);

     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const roleOrgUser = typeof user.organizations !== 'undefined' && user.organizations.length ? user.organizations[0].OrganizationUsers.role : null;

     const organizations = managedOrganizations?.filter((org) => org.role !== ORG_USER_ROLES.USER).map((item) => item.organization) ?? [];

     const orgId = orgUserLocalStorage.organizationId;

     const fetchData = () => {
          if (!organizationCourses?.length && orgId && roleOrgUser && roleOrgUser !== ORG_USER_ROLES.USER) {
               dispatch(fetchOrganizationCoursesRequest(orgId));
          }

          if (!managedOrganizations) {
               dispatch(fetchManagedOrganizationsRequest());
          }
     };

     const handleSwitchToOrganization = async () => {
          if (orgUserLocalStorage && params.organizationId && !params.orderId) {
               handleSelectManageOrg(params.organizationId, SUBSCRIBER_PATH.ORGANIZATIONS_DASHBOARD);
          }

          if (!params.organizationId || (orgUserLocalStorage && params.organizationId === orgUserLocalStorage.organizationId)) {
               return;
          }

          const myPurchasePath = params.orderId ? `${location.pathname}?orderId=${params.orderId}` : '';
          handleSelectManageOrg(params.organizationId, myPurchasePath);
     };

     useEffect(() => {
          fetchData();
          handleSwitchToOrganization();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const handleFocusDropdownMenu = () => {
          dispatch(setOverlay(true));
     };

     const handleSelectManageOrg = (organizationId, myPurchasePath) => {
          dispatch(setOrganizationSeatsFilter(null));
          dispatch(setListOrganizationSeats(null));
          dispatch(setOrganizationOrders(null));
          dispatch(setOrganization(null));

          switchOrg({
               managedOrganizations,
               organizationId,
               myPurchasePath,
               dispatch,
               navigate,
          });
     };

     const handleSelectManageUser = () => {
          if (orgUserLocalStorage) {
               localStorage.removeItem(LOCAL_ORG_USER);
               localStorage.removeItem(LOCAL_CART);
               localStorage.removeItem(LOCAL_COUPON);
               localStorage.removeItem(LOCAL_COUPON_VOUCHERS);

               addTimezoneLocal(user.timezone);
               dispatch(setCurrentCountry(user.country));
          }

          dispatch(actionClearCart());
          navigate(MY_LEARNING_PATH.DASHBOARD);
     };

     const handleCreateAccount = () => {
          setShowCreateOrg(true);
          dispatch(setListOrganizationSeats(null));
     };

     return (
          <>
               <CDropdown className="header-nav-items subscriber-nav" variant="dropdown" alignment="end" autoClose="outside">
                    <CHeaderNav className="px-3 right nav-account-profile">
                         <CDropdown className="switch-account-nav" autoClose="outside">
                              <CDropdownToggle className="switch-account-btn">
                                   <div className="account-info">
                                        <p className="account-name mb-0">{orgUserLocalStorage?.organization?.name || user.email}</p>
                                   </div>
                              </CDropdownToggle>
                              <CDropdownMenu className="account-select">
                                   <CDropdownHeader tag="div" onFocus={handleFocusDropdownMenu}>
                                        Switch between your personal account and Organizations you manage or own.
                                   </CDropdownHeader>
                                   <CDropdownItem className="d-flex align-items-center" onClick={handleSelectManageUser}>
                                        Personal Learning Account
                                        {!orgUserLocalStorage?.id && <CBadge>current</CBadge>}
                                   </CDropdownItem>

                                   <div className="dropdown-devider">
                                        <span>Organizations</span>
                                   </div>
                                   <div className="dropdown-accounts-org">
                                        {organizations?.length > 0 && (
                                             <div className="table-accounts">
                                                  <CSmartTable
                                                       items={organizations}
                                                       columns={[{ key: 'name', label: 'Account', _style: { width: '100%' } }]}
                                                       itemsPerPage={organizations.length}
                                                       tableFilter={organizations.length >= 8}
                                                       tableFilterPlaceholder="Type here to filter the list..."
                                                       scopedColumns={{
                                                            name: (item) => {
                                                                 const isActive = orgUserLocalStorage?.organizationId === item.id;

                                                                 return (
                                                                      <td key={item.id} onClick={() => handleSelectManageOrg(item.id)}>
                                                                           <CDropdownItem>
                                                                                <span className={`account-name${isActive ? ' active' : ''}`}>
                                                                                     {item.name}
                                                                                </span>
                                                                                {isActive && <CBadge className="label-current">CURRENT</CBadge>}
                                                                           </CDropdownItem>
                                                                      </td>
                                                                 );
                                                            },
                                                       }}
                                                  />
                                             </div>
                                        )}
                                   </div>
                                   <CDropdownItem tag="div" className="button-wrapper remove-hover" role="menuitem">
                                        <CButton className="btn-round-9" onClick={handleCreateAccount}>
                                             Create New Account
                                        </CButton>
                                   </CDropdownItem>
                                   <CDropdownDivider />

                                   <LoginAs />
                                   <CDropdownDivider />

                                   <CDropdownItem className="sign-out" onClick={signOut}>
                                        SIGN OUT
                                   </CDropdownItem>
                              </CDropdownMenu>
                         </CDropdown>
                    </CHeaderNav>
               </CDropdown>

               {showCreateOrg && <CreateOrganization setShowPopup={setShowCreateOrg} />}
          </>
     );
};

export default React.memo(SubscriberAccountDropdown);
